// Section 11: learning by doing (+0.15 per season worked, the `skillGain` target), apprenticeship
// (+0.35 per season in the master's job, capped at the master's level, replacing learning by
// doing while the apprentice is below it; the master works at 0.85x, skills contributor) and
// decay (-0.05 per season unused after 2 idle seasons, floor 1.0 once a skill reached 3.0).
import { activeApprentices, isWorking, peopleSorted } from '../../core/queries';
import type { Person, PersonId, PhaseContext, SkillId } from '../../core/types';
import { num } from '../shared/value';

/** Section 16 refusal: workers of a refusing household do not work this season. */
export function isRefusing(ctx: PhaseContext, p: Person): boolean {
  const c = ctx.state.counters;
  return (
    c.lastRefusalTurn === ctx.season.turn - 1 &&
    (c.refusingHouseholds ?? []).includes(p.householdId)
  );
}

function grow(ctx: PhaseContext, p: Person, skill: SkillId, gain: number, cap: number): void {
  const before = p.skills[skill] ?? 0;
  const after = Math.min(cap, ctx.cfg.skills.max, before + Math.max(0, gain));
  p.skillLastUsed[skill] = ctx.season.turn;
  if (after <= before) return;
  p.skills[skill] = after;
  if (after > (p.skillPeaks[skill] ?? 0)) p.skillPeaks[skill] = after;
  if (Math.floor(after) > Math.floor(before))
    ctx.report.add({ kind: 'skill', personId: p.id, skill, level: Math.floor(after) });
}

/** Learning by doing, or from a master (Section 11); returns the skill used this season. */
function learnAtWork(
  ctx: PhaseContext,
  p: Person,
  masterOf: Map<PersonId, PersonId>,
): SkillId | null {
  if (p.job === 'rest' || !isWorking(p) || isRefusing(ctx, p)) return null;
  const skill = p.job;
  const masterId = masterOf.get(p.id);
  const master = masterId === undefined ? undefined : ctx.state.people[masterId];
  const level = p.skills[skill] ?? 0;
  const masterLevel = master?.skills[skill] ?? 0;
  if (master !== undefined && level < masterLevel) {
    const gain = num(ctx, {
      kind: 'skillGain',
      personId: p.id,
      skill,
      via: 'apprentice',
      teacherId: master.id,
    });
    grow(ctx, p, skill, gain, masterLevel);
  } else {
    grow(ctx, p, skill, num(ctx, { kind: 'skillGain', personId: p.id, skill }), ctx.cfg.skills.max);
  }
  return skill;
}

/**
 * Skills unused for more than 2 seasons lose 0.05 per season, floor 1.0 once they reached 3.0.
 * Levels and peaks are read from the committed person (`snap`); the draft's `skillLastUsed`
 * includes this season's school and teaching, so a skill taught today does not decay.
 */
function decay(ctx: PhaseContext, snap: Person, p: Person, used: SkillId | null): void {
  const s = ctx.cfg.skills;
  for (const skill of Object.keys(snap.skills) as SkillId[]) {
    if (skill === used) continue;
    const level = snap.skills[skill] ?? 0;
    const floor = (snap.skillPeaks[skill] ?? 0) >= s.decayFloorOnceReached ? s.decayFloor : 0;
    if (level <= floor) continue;
    // This phase only ever moves skillLastUsed to now, so a skill recent in the committed person
    // is recent in the draft too; read the draft only to confirm an idle one (Section 2 budget).
    if (ctx.season.turn - (snap.skillLastUsed[skill] ?? 0) <= s.decayAfterIdleSeasons) continue;
    const idle = ctx.season.turn - (p.skillLastUsed[skill] ?? 0);
    if (idle <= s.decayAfterIdleSeasons) continue;
    const next = Math.max(floor, level - s.decayPerSeason);
    if (next !== level) p.skills[skill] = next;
  }
}

/** Does this person gain a skill or have a skill that decays this season? */
function needsWork(ctx: PhaseContext, p: Person): boolean {
  if (p.job !== 'rest' && isWorking(p)) return true;
  const s = ctx.cfg.skills;
  for (const [skill, level] of Object.entries(p.skills) as [SkillId, number][]) {
    if (level <= 0) continue;
    const floor = (p.skillPeaks[skill] ?? 0) >= s.decayFloorOnceReached ? s.decayFloor : 0;
    if (level > floor && ctx.season.turn - (p.skillLastUsed[skill] ?? 0) > s.decayAfterIdleSeasons)
      return true;
  }
  return false;
}

export function learnAndForget(ctx: PhaseContext): void {
  const masterOf = new Map<PersonId, PersonId>();
  const active = activeApprentices(ctx.cfg, ctx.state);
  for (const masterId of Object.keys(active).sort() as PersonId[])
    for (const apprentice of active[masterId] ?? []) masterOf.set(apprentice, masterId);
  // Read from the committed state; draft a person only when a skill changes.
  for (const snap of peopleSorted(ctx.state)) {
    if (!needsWork(ctx, snap)) continue;
    const p = ctx.draft.people[snap.id];
    if (p === undefined) continue;
    const used = learnAtWork(ctx, p, masterOf);
    decay(ctx, snap, p, used);
  }
}
